// src/core/puzzle.ts
import type { Puzzle, Hsl } from '../types';
import { djb2, mulberry32 } from './rng';
import { circularDelta, hslToLab } from './color';
import { deltaE2000 } from './score';

const EPOCH_MS = Date.UTC(2026, 0, 1);
const MS_PER_DAY = 86_400_000;

export function daysSinceEpoch(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d);
  return Math.round((ms - EPOCH_MS) / MS_PER_DAY);
}

// Each day gets a palette mode, which decides how the five hues are placed on
// the wheel and which saturation/lightness window they are drawn from. The mode
// is the FIRST value taken from the day's rng, so tuning any later draw leaves
// the mode sequence untouched.
export type PaletteMode = 'spread' | 'analogous' | 'pastel' | 'deep';

// Mode from a single draw: <0.55 spread, <0.75 analogous, <0.88 pastel, else deep.
export function paletteModeFor(rng: () => number): PaletteMode {
  const r = rng();
  if (r < 0.55) return 'spread';
  if (r < 0.75) return 'analogous';
  if (r < 0.88) return 'pastel';
  return 'deep';
}

// Saturation/lightness window and hue-separation floor per mode. The saturation
// floor is 20 everywhere so hue stays meaningful for scoring and for the
// perception profile; `pastel` lifts lightness, `deep` drops it.
const MODE_RANGES: Record<
  PaletteMode,
  { satMin: number; satMax: number; lightMin: number; lightMax: number; minHueSeparation: number }
> = {
  spread: { satMin: 20, satMax: 95, lightMin: 20, lightMax: 85, minHueSeparation: 40 },
  analogous: { satMin: 25, satMax: 90, lightMin: 20, lightMax: 85, minHueSeparation: 20 },
  pastel: { satMin: 25, satMax: 60, lightMin: 64, lightMax: 82, minHueSeparation: 40 },
  deep: { satMin: 30, satMax: 90, lightMin: 14, lightMax: 38, minHueSeparation: 40 },
};

// Fisher-Yates, in place, so palette position carries no information about
// which sector or arc slot a hue came from.
function shuffle(values: number[], rng: () => number): void {
  for (let i = values.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [values[i], values[j]] = [values[j], values[i]];
  }
}

function normalizeHues(hues: number[]): number[] {
  return hues.map(h => ((h % 360) + 360) % 360);
}

// Round to a whole degree, keeping the result in [0, 360): a hue just under 360
// rounds up to 360, which is the same angle as 0.
function roundHue(h: number): number {
  return Math.round(((h % 360) + 360) % 360) % 360;
}

const SPREAD_SECTOR = 72;
const SPREAD_JITTER = 32;

// Five hues spread at least 40° apart, covering the whole wheel over time.
// The circle is cut into five 72° sectors, but the whole grid is first rotated
// by a random 0–72° offset so no hue family is permanently excluded; one hue is
// then drawn from the central 32° of each sector, and the five are shuffled.
// Neighbouring picks stay at least 72° - 32° = 40° apart.
export function pick5SpreadHues(rng: () => number): number[] {
  const rotation = rng() * SPREAD_SECTOR;
  const inset = (SPREAD_SECTOR - SPREAD_JITTER) / 2;
  const hues = [0, 1, 2, 3, 4].map(
    i => rotation + i * SPREAD_SECTOR + inset + rng() * SPREAD_JITTER,
  );
  shuffle(hues, rng);
  return normalizeHues(hues);
}

const ANALOGOUS_SLOT = 30;
const ANALOGOUS_JITTER = 10;

// Five hues from one neighbourhood of the wheel: a random arc start, then five
// 30° slots, each hue sitting 0–10° into its slot, shuffled. Neighbouring picks
// stay at least 30° - 10° = 20° apart and all five fit inside a 130° arc (four
// 30° slot steps plus at most 10° of jitter on the last one).
export function pick5AnalogousHues(rng: () => number): number[] {
  const start = rng() * 360;
  const hues = [0, 1, 2, 3, 4].map(
    i => start + i * ANALOGOUS_SLOT + rng() * ANALOGOUS_JITTER,
  );
  shuffle(hues, rng);
  return normalizeHues(hues);
}

// Perceptual floor: hue separation alone does not stop two targets from looking
// the same — inside a narrow s/l window (pastels especially) neighbouring hues
// can land under ΔE2000 4, which is a coin flip to tell apart on screen. Every
// pair must be at least this far apart in CIEDE2000.
const MIN_PAIR_DELTA_E = 8;

// How many times the generator may redraw a day's palette before giving up.
const MAX_PALETTE_ATTEMPTS = 25;

// True when every pair of targets clears both floors: the mode's hue separation
// (after rounding) and the perceptual distance.
function paletteIsDistinct(targets: Hsl[], minHueSeparation: number): boolean {
  for (let i = 0; i < targets.length; i++) {
    for (let j = i + 1; j < targets.length; j++) {
      if (circularDelta(targets[i].h, targets[j].h) < minHueSeparation) return false;
      if (deltaE2000(hslToLab(targets[i]), hslToLab(targets[j])) < MIN_PAIR_DELTA_E) return false;
    }
  }
  return true;
}

// Draw order is fixed: mode, then per attempt the hues followed by saturation
// and lightness per target in palette order. Changing it changes every past
// day's puzzle.
//
// Each attempt draws a whole palette from the same rng stream and is accepted
// only if it clears both floors; a rejected attempt simply advances the stream,
// so the result stays a pure function of the date. Attempts are capped so a
// future range change that makes the floors unsatisfiable fails loudly instead
// of spinning.
export function puzzleForDate(iso: string): Puzzle {
  const seed = djb2(iso);
  const rng = mulberry32(seed);
  const mode = paletteModeFor(rng);
  const range = MODE_RANGES[mode];

  for (let attempt = 0; attempt < MAX_PALETTE_ATTEMPTS; attempt++) {
    const hues = mode === 'analogous' ? pick5AnalogousHues(rng) : pick5SpreadHues(rng);
    const targets: Hsl[] = hues.map(h => ({
      h: roundHue(h),
      s: Math.round(range.satMin + rng() * (range.satMax - range.satMin)),
      l: Math.round(range.lightMin + rng() * (range.lightMax - range.lightMin)),
    }));
    if (paletteIsDistinct(targets, range.minHueSeparation)) {
      return {
        dayNumber: daysSinceEpoch(iso),
        dateIso: iso,
        targets,
      };
    }
  }

  throw new Error(`Palette invariant broken for ${iso}`);
}
